import {
  UploadStatus,
  useVehicleGalleryContext,
} from '@/contexts/vehicle-gallery-provider';
import { toast } from '@/hooks/use-toast';
import { addElementInArray } from '@/lib/array-helpers';
import { vehicleImageStore } from '@/models/stores/vehicle-images';
import { DragDropProvider, DragOverlay, PointerSensor } from '@dnd-kit/react';
import { useState } from 'react';
import {
  type OnDragEndCallbackParam,
  type OnDragMoveCallbackParam,
  type OnDragStartCallbackParam,
  type VehicleImage,
} from '../types';
import { AssignImageCategoryBtn } from './assign-image-category-btn';
import { DraggableImage } from './draggable-image';
import { FileUploadBtn } from './file-upload-btn';
import { ForceReloadBtn } from './force-reload-btn';
import { Placeholder, PlaceholderID, PlaceholderImage } from './placeholder';

/**
 * Responsible for:
 *  - Displaying uploaded images,
 *  - Assiting with rendering an image placeholder when image is being dragged over another image.
 *  - Performing an update to vehicle image `order_index` once the images are swapped.
 */
export const Gallery = () => {
  const {
    vehicleListingID,
    isLoadingImages,
    vehicleImages,
    uploadStatus,
    setVehicleImages,
  } = useVehicleGalleryContext();
  const [isDragging, setIsDragging] = useState(false);
  const [draggedImage, setDraggedImage] = useState<VehicleImage | null>(null);

  // This event is used to "draw" drop target placeholder element that serve as visual helper where the dragged
  // element will end up if the drag is released.
  const onDragMoveCb = (event: OnDragMoveCallbackParam) => {
    const { source, target } = event.operation;

    // If user is hovering over a placeholder we don't want to do anything
    if (target?.type == 'placeholder') {
      return;
    }

    // If user is not hovering/dragging over a target, in that case we can get rid of placeholder that would
    // otherwise just occupy space
    let placeholder = vehicleImages.find((img) => img.id == PlaceholderID);
    if (placeholder && !target) {
      setVehicleImages((images) =>
        images.filter((img) => img.id != PlaceholderID),
      );
      placeholder = undefined;
    }

    // We want to put placeholder only if image is dragged over another image
    if (source && target && source.id !== target.id) {
      // When user hovers over vehicle image we want to put placeholder on its place, and move image itself
      // one place to the right.
      setVehicleImages((images) => {
        const imagesWithoutPlaceholder = images.filter(
          (img) => img.id != PlaceholderID,
        );
        const targetIndex = imagesWithoutPlaceholder.findIndex(
          (img) => img.id == target.id,
        );

        return addElementInArray(
          imagesWithoutPlaceholder,
          {
            replacedElementID: `${target.id}`,
            ...PlaceholderImage,
          },
          targetIndex,
        );
      });
    }
  };

  // Used to perform image swap if the drag ended up on placeholder
  const onDragEndCb = async (event: OnDragEndCallbackParam) => {
    setIsDragging(false);
    setDraggedImage(null);
    const { source, target } = event.operation;

    if (event.canceled) {
      return;
    }

    const sourceImg = vehicleImages.find((img) => img.id == source?.id);
    const targetIndex = vehicleImages.findIndex((img) => img.id == target?.id);

    if (
      sourceImg &&
      targetIndex != -1 &&
      uploadStatus == UploadStatus.PREPARE
    ) {
      setVehicleImages((images) => {
        images = images.filter((img) => img.type !== 'placeholder');
        images = images.filter((img) => img.id != sourceImg.id);
        images = addElementInArray(images, sourceImg, targetIndex);
        images.map((img, index) => {
          // Since we pass `order_index` to `index` parameter of `useSortable` from `dnd-kit/react/sortable` lib,
          // we need to make sure the value st`art 1 and not 0 in order to avoid weird unexpected behaviour from the lib.
          img.order_index = index + 1;
          return img;
        });

        return images;
      });

      return;
    }

    // In case update fails we want to rever images back to their original position.
    const oldImages = vehicleImages.filter((img) => img.type !== 'placeholder');
    if (
      source &&
      target &&
      source.id !== target.id &&
      target.type == 'placeholder'
    ) {
      try {
        // Placeholder is actually a temporary element in VehicleImages array that represents desired place to put the dragged element,
        // and it also help us to visually represent where the dragged image will end up.
        // But because this is a temporary element we need to grab the real target for which the placeholder was made for.
        const placeholderIndex = vehicleImages.findIndex(
          (img) => img.type === 'placeholder',
        );
        const targetImage = oldImages[placeholderIndex];

        const [updatedVehicleImages, request] = vehicleImageStore.moveImage(
          vehicleListingID,
          `${source.id}`,
          `${targetImage.id}`,
        );
        setVehicleImages(updatedVehicleImages);

        await request;

        toast({
          title: 'Success',
          description: 'Updated image position',
          className: 'bg-green-500',
        });
      } catch (err) {
        // Revert back images if update fails
        setVehicleImages(oldImages);

        toast({
          title: 'Error',
          description: 'Failed to change image position',
          variant: 'destructive',
        });
      }
    }
  };

  // This event is used soley for the purpose of keeping track if dragging of any image in the gallery has started.
  // This way images can disable their actions until the drag is completed.
  const onDragStartCb = (event: OnDragStartCallbackParam) => {
    setIsDragging(true);

    const draggedImageSource = event.operation.source;
    const draggedImage = vehicleImages.find(
      (img) => img.id == draggedImageSource?.id,
    );
    if (draggedImage) {
      setDraggedImage(draggedImage);
    }
  };

  if (isLoadingImages) {
    return <div>Loading images, plase wait...</div>;
  }

  return (
    <div className="flex flex-col mt-2">
      <div className="flex justify-between px-2 py-3">
        <p className="font-semibold text-2xl">Vehicle Images</p>

        <div className="actions flex justify-end gap-2 w-1/2">
          <AssignImageCategoryBtn />
          <FileUploadBtn />
          <ForceReloadBtn />
        </div>
      </div>

      <DragDropProvider
        sensors={[PointerSensor]}
        onDragMove={onDragMoveCb}
        onDragStart={onDragStartCb}
        onDragEnd={onDragEndCb}
      >
        <div className="w-full flex flex-wrap flex-row gap-3">
          {vehicleImages.map((img) => {
            if (img.id === PlaceholderID) {
              return <Placeholder key={img.id} id={`${img.id}`} />;
            }

            return (
              <DraggableImage
                key={img.id}
                image={img}
                disableMenu={
                  isDragging || UploadStatus.isProcessingUpload(uploadStatus)
                }
                disableDragging={UploadStatus.isProcessingUpload(uploadStatus)}
                className="rounded overflow-hidden"
              />
            );
          })}
        </div>
        <DragOverlay>
          {draggedImage && (
            <DraggableImage
              image={draggedImage}
              disableMenu
              isDragOverlay
              className="shadow-xl/30"
            />
          )}
        </DragOverlay>
      </DragDropProvider>
    </div>
  );
};

export default Gallery;
